import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { JourneyDetail } from "./types";

export type RemoveTaskInput = {
  journeyId: string;
  taskId: string;
};

type Context = {
  previousDetail: JourneyDetail | undefined;
};

// DELETE /api/admin/journeys/:journeyId/tasks/:taskId
//
// Replaces /app/admin/journeys/_actions.ts::deleteJourneyTaskAction.
// Optimistic drop-from-list — filters the matching task out of
// `detail.tasks` immediately, rolls back on error. Mirrors bucket-5
// use-remove-field.
export function useRemoveTask() {
  const queryClient = useQueryClient();
  return useMutation<null, ApiClientError, RemoveTaskInput, Context>({
    mutationFn: ({ journeyId, taskId }) =>
      apiFetch<null>(`/admin/journeys/${journeyId}/tasks/${taskId}`, {
        method: "DELETE",
      }),
    onMutate: async ({ journeyId, taskId }) => {
      const detailKey = queryKeys.journeys.detail(journeyId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<JourneyDetail>(detailKey);
      queryClient.setQueryData<JourneyDetail>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          tasks: old.tasks.filter((t) => t.id !== taskId),
        };
      });
      return { previousDetail };
    },
    onError: (err, { journeyId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.journeys.detail(journeyId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to remove task");
    },
    onSettled: (_data, _err, { journeyId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.journeys.detail(journeyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.journeys.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.tasks.all });
    },
  });
}
